import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { AutoCompleteSelectionItem, IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { AutoCompleteSelectionState } from '../../../core/component/autocomplete-remote/autocomplete-service.interface';
import { SelectionItem } from '../../../core/component/select/select.component';
import { Image } from '../../../core/model/image';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { InvoiceDeliveryMethods } from '../../../invoice/model/invoice-delivery-methods';
import { APP_CONFIG, IAppConfig } from './../../../app.config';
import { DataService } from './../../../core/service/base/data.service';
import { Company, CompanyPostTarget } from './../../model/company';
import { LocalDate } from '../../../core/model/local-date';
import { IPostTargetLoader } from '../base/post-target-loader.interface';
import { CustomProperty } from '../../model/custom-property';
import {
  CompanyAutomaticAdvicingSettings,
  CompanyFaultNotificationSettings,
  CompanyInvoiceReminderSettings,
  CompanyRentSettings,
  CompanySystemSettings,
  CompanyTerminationSettings,
  CompanySystemSettingsBulkTemplate,
  CompanySystemSettingsBulkUpdate,
  CompanyTenantPagesSettings,
} from '../../model/company-system-settings';

export const FailedToLoadLogoSrc = 'Failed to load'; // dirty hack to work around intermittent logo load errors --Nick 2026-08-03

export interface CompanyAccountPlanChangeRequest {
  accountPlanId: string;
  bankAccounts: { companyBankAccountId: string; accountId: string }[];
}

@Injectable()
export class CompanyService extends DataService<Company> implements IAutoCompleteService<Company>, IPostTargetLoader<CompanyPostTarget> {
  private readonly _defaultFilterFields: string[] = ['name'];

  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, Company, progressBar, 'company');
  }

  getSelectionItem(id: string, state: AutoCompleteSelectionState<Company>): Observable<AutoCompleteSelectionItem<Company>> {
    return this.client.get<Company>(`${this.endpoint}/selectionItem/${id}`).pipe(
      map(data => this.createAutoCompleteSelectionItem(data)),
      catchError(e => {
        this.notification.showError(e);
        return [];
      })
    );
  }

  getAll(): Observable<Company[]> {
    this.progressBar.start();
    return this.client.get<Company[]>(`${this.endpoint}/all`).pipe(
      map(response => this.toModels(response)),
      finalize(() => this.progressBar.stop())
    );
  }

  getFilterDefinition(): Observable<{
    name: string;
    number: string;
    organizationNo: string;
    selectedFields: string[];
    defaultFields: string[];
    companyCustomPropertiesTextValues: { key: string; value: string }[];
    companyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    companyCustomPropertiesNumericValues: { key: string; value: string }[];
    companyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => {
        return {
          name: res.name ? res.name : '',
          number: res.number ? res.number : '',
          organizationNo: res.organizationNo ? res.organizationNo : '',
          selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._defaultFilterFields,
          defaultFields: this._defaultFilterFields,
          companyCustomPropertiesTextValues: Array.isArray(res.companyCustomPropertiesTextValues)
            ? res.companyCustomPropertiesTextValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesNumericValues: Array.isArray(res.companyCustomPropertiesNumericValues)
            ? res.companyCustomPropertiesNumericValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesDateValues: Array.isArray(res.companyCustomPropertiesDateValues)
            ? res.companyCustomPropertiesDateValues.map(v => ({
                key: v.key,
                value: LocalDate.fromDate(v.value),
              }))
            : [],
          companyCustomPropertiesSelectionValues: Array.isArray(res.companyCustomPropertiesSelectionValues)
            ? res.companyCustomPropertiesSelectionValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesBooleanValues: Array.isArray(res.companyCustomPropertiesBooleanValues)
            ? res.companyCustomPropertiesBooleanValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  createAutoCompleteSelectionItem(data: Company): AutoCompleteSelectionItem<Company> {
    if (!data) {
      return new AutoCompleteSelectionItem('', null, null);
    }
    const name = `${data.number} ${data.name}`;
    const shortName = data.name;
    return new AutoCompleteSelectionItem(name, data.id, Object.assign(new Company(), data), shortName);
  }

  getSelection(filter: string, options?: any, page?: number, maxItems?: number): Observable<AutoCompleteSelectionItem<Company>[]> {
    const queryArgs = {
      filter: filter,
      maxItems: maxItems || 20,
    };
    if (options) queryArgs['options'] = options;
    if (page > 0) queryArgs['page'] = page;

    return this.client.post<Company[]>(`${this.endpoint}/selection`, queryArgs).pipe(
      map(data => data.map(item => this.createAutoCompleteSelectionItem(item))),
      catchError(e => {
        this.notification.showError(e);
        return [];
      })
    );
  }

  getInvoiceDeliveryMethods(companyId?: string, contactId?: string): Observable<InvoiceDeliveryMethods> {
    const params =
      !!companyId && !!contactId
        ? {
            params: new HttpParams().append('company', companyId).append('contact', contactId),
          }
        : undefined;
    return this.client.get<InvoiceDeliveryMethods>(`${this.endpoint}/delivery-methods`, params).pipe(
      map(data => Object.assign(new InvoiceDeliveryMethods(), data)),
      catchError(e => {
        this.notification.showError(e);
        return [];
      })
    );
  }

  getFortnoxRegisterUrl(companyId: string): Observable<string> {
    return this.client.put<string>(`${this.endpoint}/getFortnoxRegisterUrl`, { companyId: companyId });
  }

  getVismaRegisterUrl(companyId: string): Observable<string> {
    return this.client.put<string>(`${this.endpoint}/getVismaRegisterUrl`, { companyId: companyId });
  }

  getOpenBusinessUrl(): Observable<string> {
    return this.client.get<string>(`${this.endpoint}/hogia-ob-url`);
  }

  registerVismaControlEdge(companyId: string): Observable<string> {
    return this.client.put<string>(`${this.endpoint}/registervismacontroledge`, { companyId: companyId });
  }

  registerHogiaKivra(companyId: string): Observable<string> {
    return this.client.put<string>(`${this.endpoint}/registerhogiakivra`, { companyId: companyId });
  }

  private createMultiPartFormData(company: Company, includeAccountPlan = true): FormData {
    const formData = new FormData();
    const model: any = company.toJSON();
    if (!includeAccountPlan) delete model.accountPlanId;
    formData.append('jsonData', JSON.stringify(model));
    formData.append('logo', company.logo.source, company.logo.source.name);
    return formData;
  }

  protected createModel(data: any): Company {
    const model = super.createModel(data);
    if (typeof data.logoSrc === 'string' && data.logoSrc !== FailedToLoadLogoSrc) {
      model.logo = new Image(data.logoSrc);
    }
    return model;
  }

  getBusinessCountryCultureSelection(): SelectionItem[] {
    return [
      new SelectionItem('COUNTRY.SWEDEN', 'sv-SE'),
      new SelectionItem('COUNTRY.DENMARK', 'da-DK'),
      new SelectionItem('COUNTRY.FINLAND', 'fi-FI'),
    ];
  }

  add(model: Company): Observable<any> {
    this.progressBar.start();
    if (model.logo?.source) {
      return this.client.post(this.endpoint, this.createMultiPartFormData(model)).pipe(finalize(() => this.progressBar.stop()));
    }
    return super.add(model);
  }

  change(model: Company): Observable<any> {
    this.progressBar.start();
    if (model.logo?.source) {
      return this.client.put(this.endpoint, this.createMultiPartFormData(model, false)).pipe(finalize(() => this.progressBar.stop()));
    }
    let jsonModel: any = model.toJSON();
    delete jsonModel.accountPlanId;
    if (!jsonModel.logo && model.logoSrc !== FailedToLoadLogoSrc) {
      jsonModel = Object.assign({ deleteLogoSrc: true }, jsonModel);
    }
    return super.changeAny(jsonModel);
  }

  changeAccountPlan(companyId: string, model: CompanyAccountPlanChangeRequest): Observable<void> {
    this.progressBar.start();
    return this.client.put<void>(`${this.endpoint}/${companyId}/account-plan`, model).pipe(
      tap(() => this.notification.showSuccess()),
      catchError(error => {
        this.notification.showError(error);
        throw error;
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  getUsedCurrencies(): Observable<string[]> {
    return this.client.get<string[]>(this.endpoint + '/usedCurrencies');
  }

  getNewDefaults(): Observable<any> {
    return this.client.get(this.endpoint + '/new');
  }

  validateCompany(company: string): Observable<string> {
    return this.client.get<string>(`${this.endpoint}/validate-name`, { params: new HttpParams().append('name', company) });
  }

  disableAccountingIntegration(companyId: string): Observable<string> {
    return this.client
      .put<string>(`${this.endpoint}/cancel-accounting-integration/${companyId}`, {})
      .pipe(tap(() => this.notification.showSuccess()));
  }

  downloadGl07(companyId: string): Observable<File> {
    this.progressBar.start();
    return this.getFile(`${this.endpoint}/download-gl07/${companyId}`, 'document').pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  downloadRaindance(companyId: string): Observable<File> {
    this.progressBar.start();
    return this.getFile(`${this.endpoint}/download-raindance/${companyId}`, 'document').pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  testFtp(
    ftpProtocol: string,
    ftpAddress: string,
    ftpPort: number,
    ftpUser: string,
    ftpPassword: string,
    ftpPath: string
  ): Observable<string> {
    return this.client
      .post<string>(
        `${this.endpoint}/test-ftp`,
        JSON.stringify({
          ftpProtocol: ftpProtocol,
          ftpAddress: ftpAddress,
          ftpPort: ftpPort,
          ftpUser: ftpUser,
          ftpPassword: ftpPassword,
          ftpPath: ftpPath,
        })
      )
      .pipe(tap(() => this.notification.showSuccess('SUCCESS.FTP_CONNECTION')));
  }

  sendToProvider(companyId: string): Observable<string> {
    this.progressBar.start();

    return this.client.put<string>(`${this.endpoint}/send-accounting/${companyId}`, {}).pipe(
      tap(() => this.notification.showSuccess('SUCCESS.TRANSFER_ACCOUNTING_SUCCESS')),
      catchError(e => {
        this.notification.showError(e);
        throw e;
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  sendToDebtCollection(companyId: string): Observable<string> {
    this.progressBar.start();
    return this.client.put<string>(`${this.endpoint}/send-debt-collection/${companyId}`, {}).pipe(
      tap(() => this.notification.showSuccess('SUCCESS.CASE_SENT_TO_DEBT_COLLECTION')),
      catchError(e => {
        this.notification.showError(e);
        throw e;
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  getSystemSettings(companyId: string): Observable<CompanySystemSettings> {
    this.progressBar.start();
    return this.client
      .get<CompanySystemSettings>(`${this.endpoint}/${companyId}/system-settings`)
      .pipe(finalize(() => this.progressBar.stop()));
  }

  getSystemSettingsBulkTemplate(model: CompanySystemSettingsBulkTemplate): Observable<CompanySystemSettings> {
    this.progressBar.start();
    return this.client
      .post<CompanySystemSettings>(`${this.endpoint}/system-settings/bulk/template`, model)
      .pipe(finalize(() => this.progressBar.stop()));
  }

  updateInvoiceReminderSettings(companyId: string, settings: CompanyInvoiceReminderSettings): Observable<void> {
    this.progressBar.start();
    return this.client.put<void>(`${this.endpoint}/${companyId}/system-settings/invoice-reminder`, settings).pipe(
      tap(() => this.notification.showSuccess()),
      finalize(() => this.progressBar.stop())
    );
  }

  updateAutomaticAdvicingSettings(companyId: string, settings: CompanyAutomaticAdvicingSettings): Observable<void> {
    this.progressBar.start();
    return this.client.put<void>(`${this.endpoint}/${companyId}/system-settings/automatic-advicing`, settings).pipe(
      tap(() => this.notification.showSuccess()),
      finalize(() => this.progressBar.stop())
    );
  }

  updateFaultNotificationSettings(companyId: string, settings: CompanyFaultNotificationSettings): Observable<void> {
    this.progressBar.start();
    return this.client.put<void>(`${this.endpoint}/${companyId}/system-settings/fault-notification`, settings).pipe(
      tap(() => this.notification.showSuccess()),
      finalize(() => this.progressBar.stop())
    );
  }

  updateTenantPagesSettings(companyId: string, settings: CompanyTenantPagesSettings): Observable<void> {
    this.progressBar.start();
    return this.client.put<void>(`${this.endpoint}/${companyId}/system-settings/tenant-pages`, settings).pipe(
      tap(() => this.notification.showSuccess()),
      finalize(() => this.progressBar.stop())
    );
  }

  updateTerminationSettings(companyId: string, settings: CompanyTerminationSettings): Observable<void> {
    this.progressBar.start();
    return this.client.put<void>(`${this.endpoint}/${companyId}/system-settings/termination`, settings).pipe(
      tap(() => this.notification.showSuccess()),
      finalize(() => this.progressBar.stop())
    );
  }

  updateRentSettings(companyId: string, settings: CompanyRentSettings): Observable<void> {
    this.progressBar.start();
    return this.client.put<void>(`${this.endpoint}/${companyId}/system-settings/rent`, settings).pipe(
      tap(() => this.notification.showSuccess()),
      finalize(() => this.progressBar.stop())
    );
  }

  bulkUpdateSystemSettings(model: CompanySystemSettingsBulkUpdate): Observable<number> {
    this.progressBar.start();
    return this.client.put<number>(`${this.endpoint}/system-settings/bulk`, model).pipe(
      tap(() => this.notification.showSuccess()),
      catchError(e => {
        this.notification.showError(e);
        throw e;
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  getImdFtpUserName(companyId: string): Observable<string> {
    return this.client.get<string>(`${this.endpoint}/imdftpuser/${companyId}`);
  }

  setAccountingLockedTo(ids: string[], lockedTo: LocalDate): Observable<any> {
    return this.client.post<any>(`${this.endpoint}/set-accounting-lock-to`, { ids, lockedTo }).pipe(
      catchError(error => {
        this.notification.showError(error);
        throw new Error();
      })
    );
  }

  export(ids: string[]): Observable<File> {
    return super.exportDataItems(ids, 'Foretag.xlsx');
  }

  getPostTargetModels(): Observable<CompanyPostTarget[]> {
    this.progressBar.start();
    return this.client.get<CompanyPostTarget[]>(`${this.endpoint}/post-target`).pipe(
      map(response => response.map(x => new CompanyPostTarget(x))),
      finalize(() => this.progressBar.stop())
    );
  }
}
